import { useId } from "react";

export default function GenderTransWomanIconMini({
  size = 28,
  active = false,
  title = "Trans Woman",
  ...rest
}) {
  const uid = useId();
  const id = (name) => `${uid}-${name}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      style={{ display: "block" }}
      data-preserve
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Outer glow around rim */}
        <radialGradient id={id("rimGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#1a0010" stopOpacity="0" />
          <stop offset="85%" stopColor="#FF6DA8" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#6BD9FF" stopOpacity="0.75" />
        </radialGradient>

        {/* Rim stroke gradient (pink–white–blue) */}
        <linearGradient
          id={id("rimStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="40%" stopColor="#FFFFFF" />
          <stop offset="80%" stopColor="#6BD9FF" />
          <stop offset="100%" stopColor="#FF6DA8" />
        </linearGradient>

        {/* Inner face */}
        <radialGradient id={id("faceFill")} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#1b1024" />
          <stop offset="70%" stopColor="#08040f" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Rim highlight */}
        <radialGradient id={id("rimHighlight")} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Symbol stroke gradient */}
        <linearGradient
          id={id("symbolStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="35%" stopColor="#FFFFFF" />
          <stop offset="70%" stopColor="#6BD9FF" />
          <stop offset="100%" stopColor="#FF6DA8" />
        </linearGradient>

        {/* Symbol glow */}
        <filter
          id={id("symbolGlow")}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="b1" />
          <feColorMatrix
            in="b1"
            type="matrix"
            values="
              1 0 0 0 0.8
              0 1 0 0 0.7
              0 0 1 0 0.9
              0 0 0 1 0
            "
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Active aura */}
        <filter
          id={id("activeAura")}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="6" result="b1" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Spark glows */}
        <filter id={id("spark")} x="-200%" y="-200%" width="400%" height="400%">
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>

      {/* ===== Badge Base ===== */}
      <g filter={active ? `url(#${id("activeAura")})` : undefined}>
        <circle cx="32" cy="32" r="30" fill={`url(#${id("rimGlow")})`} />
        <circle
          cx="32"
          cy="32"
          r="30"
          fill="none"
          stroke={`url(#${id("rimStroke")})`}
          strokeWidth="4"
        />
        <circle
          cx="32"
          cy="24"
          r="26"
          fill={`url(#${id("rimHighlight")})`}
          opacity="0.4"
        />
        <circle cx="32" cy="32" r="24" fill={`url(#${id("faceFill")})`} />
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.16"
          strokeWidth="1.25"
        />
      </g>

      {/* ===== Trans Woman Symbol (Venus with a star accent) ===== */}
      <g
        filter={`url(#${id("symbolGlow")})`}
        stroke={`url(#${id("symbolStroke")})`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Circle */}
        <circle cx="28" cy="29" r="7" />

        {/* Vertical line */}
        <line x1="28" y1="36" x2="28" y2="42" />

        {/* Cross bar */}
        <line x1="24" y1="39" x2="32" y2="39" />

        {/* Little sparkle/star near top-right (transition energy) */}
        <path
          d="M39 20 L41 22 L43 20 L41 18 Z"
          strokeWidth="2"
          stroke={`url(#${id("symbolStroke")})`}
        />
      </g>

      {/* ===== Twinkling sparks ===== */}
      <g opacity="0.9">
        <circle
          cx="20"
          cy="22"
          r="0.8"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="44"
          cy="18"
          r="0.6"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="48"
          cy="36"
          r="0.7"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="18"
          cy="40"
          r="0.5"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
      </g>
    </svg>
  );
}
